import {
  AttributionCodeSchema,
  PhoneE164Schema,
  type WhatsappConsentState,
  type WhatsappLinkDto,
} from '@devmil/contracts';
import {useId, useState, type FormEvent} from 'react';

import {usePanel} from '../../app/PanelProvider.js';
import {useReauth} from '../../auth/ReauthContext.js';
import {formatDateTime} from '../../lib/format.js';
import {describeProblem, type MissingProof} from '../../lib/panel-problem.js';

/**
 * Resgate manual do código do WhatsApp.
 *
 * Três regras mandam neste componente:
 *
 * 1. **Código e telefone só existem no estado do formulário e no corpo do
 *    POST.** Nunca em URL, nunca em `localStorage` ou `sessionStorage`, nunca
 *    em analytics. Por isso o `submit` do formulário é cancelado antes de
 *    qualquer coisa: um envio nativo sem `method` vira `GET` e joga os dois
 *    campos na query string, que é o pior lugar possível para um segredo,
 *    porque sobrevive no histórico do navegador e no log do servidor.
 * 2. **Sucesso limpa os dois campos.** Código esquecido na tela é código
 *    exposto a quem passar atrás da cadeira, e o valor já não serve para nada:
 *    ele é de uso único.
 * 3. **A tela só mostra pseudônimo e 4 dígitos.** O telefone digitado some do
 *    estado no sucesso; o que fica é o `phoneLast4` que a API devolve.
 */

/**
 * Consentimento que permite tentar o resgate.
 *
 * Lista de PERMISSÃO: `not_asked`, `revoked` e qualquer estado que apareça
 * amanhã caem no lado que não deixa tentar. Uma lista de bloqueio liberaria
 * sozinha o estado novo, e o estado novo seria descoberto pelo titular.
 */
const CONSENTIMENTO_QUE_PERMITE: ReadonlySet<WhatsappConsentState> = new Set([
  'granted',
]);

export const WhatsappRedeemForm = ({
  pseudonym,
  consent,
  onLinked,
}: {
  pseudonym: string;
  consent: WhatsappConsentState;
  onLinked: () => void;
}) => {
  const {dataSource} = usePanel();
  const reauth = useReauth();
  const codeId = useId();
  const phoneId = useId();
  const headingId = useId();

  const [code, setCode] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [missingProof, setMissingProof] = useState<MissingProof | null>(null);
  const [link, setLink] = useState<WhatsappLinkDto | null>(null);

  const permitido = CONSENTIMENTO_QUE_PERMITE.has(consent);
  const disabled = !permitido || busy;

  // Separado do handler de submit para o diálogo de reauth poder repetir o
  // resgate depois do passkey confirmado, pelo MESMO caminho, validação
  // incluída.
  const tentarResgate = () => {
    if (disabled) {
      return;
    }

    // Recusar, e não limpar. Máscara, espaço e traço não são corrigidos aqui:
    // um telefone "consertado" pela tela associa o canal errado, e associar o
    // canal errado é mandar mensagem para quem não pediu.
    const codigo = AttributionCodeSchema.safeParse(code);
    const telefone = PhoneE164Schema.safeParse(phone);
    if (!codigo.success || !telefone.success) {
      setLink(null);
      setMissingProof(null);
      setProblem(
        !codigo.success
          ? 'O código tem 22 caracteres, sem espaço nem acento. Copie o valor inteiro.'
          : 'O telefone precisa estar em E.164, começando com + e o código do país.',
      );
      return;
    }

    setBusy(true);
    setProblem(null);
    setMissingProof(null);
    void dataSource
      .redeemWhatsappCode({
        pseudonym,
        code: codigo.data,
        whatsappPhoneE164: telefone.data,
      })
      .then((resultado) => {
        setLink(resultado);
        setCode('');
        setPhone('');
        onLinked();
      })
      .catch((erro: unknown) => {
        const descrito = describeProblem(erro);
        setProblem(descrito.message);
        setMissingProof(descrito.missingProof);
        if (descrito.missingProof === 'passkey_reauth' && reauth.armado) {
          reauth.oferecer({message: descrito.message, retry: tentarResgate});
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    // Primeira linha, sempre: se algo abaixo lançar, o navegador não pode ter
    // começado a navegação que levaria código e telefone para a URL.
    event.preventDefault();
    tentarResgate();
  };

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Resgatar código do WhatsApp</h2>
        <span className="dm-illustrative">USO ÚNICO</span>
      </div>

      {permitido ? null : (
        <p className="dm-subtitle">
          O resgate só abre com consentimento concedido. Sem isso, associar o
          canal seria falar com quem não autorizou.
        </p>
      )}

      <form className="dm-form" onSubmit={submit} noValidate>
        <div className="dm-field">
          <label htmlFor={codeId}>Código enviado ao titular</label>
          <input
            id={codeId}
            type="text"
            value={code}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            maxLength={22}
            onChange={(event) => {
              setCode(event.target.value);
            }}
          />
        </div>

        <div className="dm-field">
          <label htmlFor={phoneId}>Telefone do WhatsApp, em E.164</label>
          <input
            id={phoneId}
            type="text"
            value={phone}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            inputMode="tel"
            maxLength={16}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
        </div>

        <div className="dm-actions">
          <button
            className="dm-action dm-action-primary"
            type="submit"
            disabled={disabled}
          >
            Resgatar e associar
          </button>
        </div>
      </form>

      {problem ? (
        <div className="dm-alert" role="alert">
          {problem}
          {missingProof === 'passkey_reauth' ? (
            <>
              {' '}
              A sessão continua aberta, e sessão aberta não é a mesma prova que
              o passkey.
            </>
          ) : null}
        </div>
      ) : null}

      {link ? (
        <ul className="dm-checklist">
          <li>
            <span>Canal associado</span>
            <strong className="dm-ok">FINAL {link.phoneLast4}</strong>
          </li>
          <li>
            <span>Associado em</span>
            <strong>{formatDateTime(link.linkedAt)}</strong>
          </li>
        </ul>
      ) : null}
    </section>
  );
};
